import { useId, type ReactNode } from 'react';
import type { Avatar } from '@il-colpo/shared/account';
import { colors } from '@il-colpo/shared/design';

/**
 * The player's silhouette, drawn from its few named parts (`@il-colpo/shared/account` avatar): a
 * noir bust against the night, lit from one side by the colour the player picked. Pure SVG, no
 * images (asset key `ui.avatar.figure`: hand-made art can take its place in the manifest).
 */

const COATS: Record<Avatar['coat'], string> = {
  ink: '#262a52',
  navy: '#27498c',
  plum: '#7a3068',
  olive: '#56652d',
  brown: '#7a4c2b',
  grey: '#737988',
  crimson: '#8d1f3a',
  ivory: '#d6cfba',
};

const RIMS: Record<Avatar['rim'], string> = {
  cyan: colors.neonCyan,
  amber: colors.neonAmber,
  green: colors.neonGreen,
  pink: colors.neonPink,
  chalk: colors.bpChalk,
  violet: '#A98BFF',
  gold: '#F2C14E',
  ghost: '#D5EEF7',
};

const SILHOUETTE = '#0d0d22';
const HAT = '#15152f';

/** Points of a five-pointed star centred on (cx, cy). */
function starPath(cx: number, cy: number, outer: number, inner: number): string {
  const points: string[] = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    points.push(
      `${(cx + radius * Math.cos(angle)).toFixed(2)} ${(cy + radius * Math.sin(angle)).toFixed(2)}`,
    );
  }
  return `M${points.join('L')}Z`;
}

function Emblem({ suit, fill }: { suit: Avatar['suit']; fill: string }): ReactNode {
  switch (suit) {
    case 'spade':
      return (
        <path
          d="M50 77 C43 84 40 87 44 90.5 C46.2 92.2 49 91 50 89 C51 91 53.8 92.2 56 90.5 C60 87 57 84 50 77Z M50 89 L48 95 H52Z"
          fill={fill}
        />
      );
    case 'heart':
      return (
        <path
          d="M50 93 C41 87 41 79 46.5 79 C48.5 79 50 80.5 50 82 C50 80.5 51.5 79 53.5 79 C59 79 59 87 50 93Z"
          fill={fill}
        />
      );
    case 'diamond':
      return <path d="M50 77 L57.5 85.5 L50 94 L42.5 85.5Z" fill={fill} />;
    case 'club':
      return (
        <g fill={fill}>
          <circle cx="50" cy="80.5" r="3.6" />
          <circle cx="46" cy="86" r="3.6" />
          <circle cx="54" cy="86" r="3.6" />
          <path d="M50 85 L48.2 94 H51.8Z" />
        </g>
      );
    case 'star':
      return <path d={starPath(50, 86, 8.5, 3.6)} fill={fill} />;
    case 'crown':
      return (
        <g fill={fill}>
          <path d="M40 92 L41.5 81 L46 86 L50 78 L54 86 L58.5 81 L60 92Z" />
          <rect x="40" y="92" width="20" height="2.4" rx="0.6" />
        </g>
      );
  }
}

function Hat({ hat, rim }: { hat: Avatar['hat']; rim: string }): ReactNode {
  switch (hat) {
    case 'fedora':
      return (
        <g>
          <path d="M36 34 C35 17 65 17 64 34Z" fill={HAT} />
          <path d="M36 31.5 H64 V34 H36Z" fill={rim} opacity="0.9" />
          <ellipse cx="50" cy="34" rx="29" ry="5.5" fill={HAT} />
          <path
            d="M21 34 C26 37 74 37 79 34"
            fill="none"
            stroke={rim}
            strokeWidth="1.2"
            opacity="0.7"
          />
        </g>
      );
    case 'cap':
      return (
        <g>
          <path d="M34 36 C33 18 67 18 66 36Z" fill={HAT} />
          <path d="M60 34 C70 33 79 36 80 39 C72 38 66 38 60 38Z" fill={HAT} />
          <path d="M34 35.5 H66" stroke={rim} strokeWidth="1.4" opacity="0.8" />
        </g>
      );
    case 'beret':
      return (
        <g>
          <ellipse cx="52" cy="28" rx="21" ry="8.5" transform="rotate(-8 52 28)" fill={HAT} />
          <circle cx="56" cy="19.5" r="1.8" fill={HAT} />
          <path
            d="M33 33 C40 36 60 36 67 33"
            fill="none"
            stroke={rim}
            strokeWidth="1.2"
            opacity="0.7"
          />
        </g>
      );
    case 'bowler':
      return (
        <g>
          <path d="M35 34 C34 14 66 14 65 34Z" fill={HAT} />
          <path d="M35 31 H65 V33.5 H35Z" fill={rim} opacity="0.85" />
          <ellipse cx="50" cy="34" rx="24" ry="4" fill={HAT} />
        </g>
      );
    case 'headphones':
      return (
        <g>
          <path d="M33.5 46 C32 20 68 20 66.5 46" fill="none" stroke="#2a2d58" strokeWidth="4.5" />
          <rect x="28" y="40" width="8" height="14" rx="3.5" fill={rim} />
          <rect x="64" y="40" width="8" height="14" rx="3.5" fill={rim} />
        </g>
      );
    case 'tophat':
      return (
        <g>
          <rect x="37.5" y="8" width="25" height="26" rx="1.5" fill={HAT} />
          <rect x="37.5" y="26" width="25" height="4.5" fill={rim} opacity="0.9" />
          <ellipse cx="50" cy="34" rx="27" ry="5" fill={HAT} />
          <ellipse cx="50" cy="8" rx="12.5" ry="2.4" fill="#26264a" />
        </g>
      );
    case 'panama':
      return (
        <g>
          <path d="M36 34 C35 17 65 17 64 34Z" fill="#d8c48a" />
          <path d="M36 30.5 H64 V33.5 H36Z" fill={HAT} />
          <ellipse cx="50" cy="34" rx="30" ry="6" fill="#e6d49c" />
          <path
            d="M20 34.5 C26 38 74 38 80 34.5"
            fill="none"
            stroke={rim}
            strokeWidth="1.2"
            opacity="0.75"
          />
        </g>
      );
    case 'hood':
      return (
        <g>
          <path
            d="M31 58 C27 26 40 12 50 12 C60 12 73 26 69 58 C66 46 62 38 50 36 C38 38 34 46 31 58Z"
            fill={HAT}
          />
          <path
            d="M31 58 C27 26 40 12 50 12 C60 12 73 26 69 58"
            fill="none"
            stroke={rim}
            strokeWidth="1.4"
            opacity="0.8"
          />
        </g>
      );
    case 'none':
      return <path d="M35 40 C34 24 66 24 65 40 C60 33 40 33 35 40Z" fill="#090919" />;
  }
}

function Mask({ mask, rim }: { mask: Avatar['mask']; rim: string }): ReactNode {
  switch (mask) {
    case 'none':
      return null;
    case 'domino':
      return (
        <g>
          <path d="M35.5 40 Q50 35 64.5 40 L64.5 47 Q50 42.5 35.5 47Z" fill="#04040d" />
          <ellipse cx="43.5" cy="42.8" rx="2.6" ry="1.2" fill={rim} />
          <ellipse cx="56.5" cy="42.8" rx="2.6" ry="1.2" fill={rim} />
        </g>
      );
    case 'bandana':
      return (
        <g>
          <path d="M35.5 49 Q50 58 64.5 49 L64.5 58 Q50 69 35.5 58Z" fill={rim} opacity="0.88" />
          <circle cx="63" cy="55" r="3" fill={rim} opacity="0.88" />
        </g>
      );
    case 'visor':
      return (
        <g>
          <rect x="34.5" y="38.5" width="31" height="7" rx="3" fill={rim} opacity="0.92" />
          <rect x="37" y="40" width="10" height="1.6" rx="0.8" fill="#ffffff" opacity="0.55" />
        </g>
      );
    case 'fox':
      return (
        <g>
          <path d="M36 38 L34 24 L45 34Z M64 38 L66 24 L55 34Z" fill="#d9722b" />
          <path d="M35.5 38.5 Q50 33 64.5 38.5 L60.5 53 Q50 60 39.5 53Z" fill="#d9722b" />
          <path d="M41 53 Q50 59 59 53 L56 49 Q50 52 44 49Z" fill="#f3ead8" />
          <ellipse cx="43.5" cy="43.5" rx="3" ry="1.7" fill="#0b0b1c" />
          <ellipse cx="56.5" cy="43.5" rx="3" ry="1.7" fill="#0b0b1c" />
          <circle cx="50" cy="50.2" r="1.6" fill="#0b0b1c" />
        </g>
      );
    case 'skull':
      return (
        <g>
          <path
            d="M35.5 44 C35.5 30 64.5 30 64.5 44 C64.5 52 61 55 59 57 L59 61 H41 L41 57 C39 55 35.5 52 35.5 44Z"
            fill="#e9e9f0"
          />
          <ellipse cx="43.5" cy="44" rx="3.6" ry="4.2" fill="#0b0b1c" />
          <ellipse cx="56.5" cy="44" rx="3.6" ry="4.2" fill="#0b0b1c" />
          <path d="M50 49 L47.8 53.5 H52.2Z" fill="#0b0b1c" />
          <path d="M44.5 57 V61 M50 57 V61 M55.5 57 V61" stroke="#0b0b1c" strokeWidth="1.2" />
        </g>
      );
  }
}

export interface AvatarFigureProps {
  avatar: Avatar;
  /** Any CSS length; the figure is square. */
  size?: string;
  /** What a screen reader says. Leave out when the name is shown next to it. */
  label?: string;
}

export function AvatarFigure({ avatar, size = '4rem', label }: AvatarFigureProps) {
  const uid = useId().replace(/:/g, '');
  const rim = RIMS[avatar.rim];
  const coat = COATS[avatar.coat];
  const glow = `${uid}-glow`;
  const clip = `${uid}-clip`;
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      data-asset="ui.avatar.figure"
      style={{ display: 'block', flex: 'none' }}
    >
      <defs>
        <radialGradient id={glow} cx="30%" cy="30%" r="90%">
          <stop offset="0" stopColor={rim} stopOpacity="0.38" />
          <stop offset="0.55" stopColor={colors.night800} />
          <stop offset="1" stopColor={colors.ink900} />
        </radialGradient>
        <clipPath id={clip}>
          <circle cx="50" cy="50" r="47" />
        </clipPath>
      </defs>
      <circle cx="50" cy="50" r="47" fill={`url(#${glow})`} />
      <g clipPath={`url(#${clip})`}>
        {/* shoulders and coat */}
        <path d="M10 104 C10 76 28 66 50 66 C72 66 90 76 90 104Z" fill={coat} />
        <path
          d="M10 104 C10 76 28 66 50 66"
          fill="none"
          stroke={rim}
          strokeWidth="2"
          opacity="0.9"
        />
        <path d="M39 67 L50 84 L61 67 L50 70Z" fill="#000" opacity="0.35" />
        {/* neck and head */}
        <rect x="43.5" y="55" width="13" height="14" rx="3" fill={SILHOUETTE} />
        <ellipse cx="50" cy="44" rx="15" ry="18" fill={SILHOUETTE} />
        <path
          d="M36.2 40 A15 18 0 0 0 41 59"
          fill="none"
          stroke={rim}
          strokeWidth="1.6"
          opacity="0.85"
        />
        <Mask mask={avatar.mask} rim={rim} />
        <Hat hat={avatar.hat} rim={rim} />
        <Emblem suit={avatar.suit} fill={rim} />
      </g>
      <circle cx="50" cy="50" r="47" fill="none" stroke={rim} strokeWidth="2.5" opacity="0.9" />
    </svg>
  );
}
